/**
 * LOS Application Data — TypeScript types and Zod schema.
 *
 * This file defines the canonical shape of LOS data attached to one
 * loan file. The shape is identical regardless of the adapter
 * (json_upload for demo, rest_api in production).
 *
 * Where this type is used:
 *   - shared/src/schemas/los.ts          — runtime Zod validation of uploads
 *   - backend/src/modules/los/*          — service layer (Batch 2)
 *   - validation-core/src/context.ts     — attached to RuleContext (Batch 3)
 *   - validation-core/src/rules/uc1-los-checks.ts — read by 12 LOS rules (Batch 3)
 *
 * When real LOS API integration ships, only the adapter that *produces*
 * this type changes; this contract stays stable.
 */
import { z } from 'zod';
import { LOS_API_STATUS, POSIDEX_STATUS, HUNTER_RESULT, BQS_RESULT, RCU_TYPE, RCU_OUTCOME, DEVIATION_STATUS, DEVIATION_TYPE, APPLICANT_ROLE, E_AGREEMENT_INTERFACE_STATUS, ASSESSMENT_METHOD, } from '../constants/los.js';
// -------------------------------------------------------------------------
// Sub-shapes (Zod first, types derived via z.infer)
// -------------------------------------------------------------------------
const enumFromConst = (obj) => z.enum(Object.values(obj));
const ApplicantSchema = z.object({
    applicantId: z.string().min(1),
    role: enumFromConst(APPLICANT_ROLE),
    name: z.string().min(1),
    pan: z.string().optional().nullable(),
    aadhar: z.string().optional().nullable(), // typically masked, e.g. "XXXX-XXXX-1234"
    dob: z.string().optional().nullable(), // ISO date "YYYY-MM-DD"
    mobile: z.string().optional().nullable(),
    kycDocuments: z.array(z.string()).default([]), // free-form: "pan", "aadhar", "passport"...
    posidexStatus: enumFromConst(POSIDEX_STATUS),
    cibilScore: z.number().int().min(0).max(1000).nullable(),
    cibilStatus: z.string().optional().nullable(),
});
const ApiStatusEntrySchema = z.object({
    status: enumFromConst(LOS_API_STATUS),
    validated: z.boolean(),
});
// Entries are individually optional: the real LOS `underwriting_info`
// payload only exposes PAN_VERIFICATION and CKYC_CHECK. Absent entries mean
// "not reported by LOS", which rules must treat as inconclusive.
const ApiStatusDetailsSchema = z.object({
    panCard: ApiStatusEntrySchema.optional().nullable(),
    aadhar: ApiStatusEntrySchema.optional().nullable(),
    eMandate: ApiStatusEntrySchema.optional().nullable(),
    ckyc: ApiStatusEntrySchema.optional().nullable(),
    panProfile: ApiStatusEntrySchema.optional().nullable(),
});
const HunterSchema = z.object({
    result: enumFromConst(HUNTER_RESULT),
    errorDescription: z.string().optional().nullable(),
    matchFound: z.boolean(),
});
const BqsSchema = z.object({
    result: enumFromConst(BQS_RESULT),
    matchDetails: z.string().optional().nullable(),
});
const RcuSchema = z.object({
    type: enumFromConst(RCU_TYPE),
    outcome: enumFromConst(RCU_OUTCOME),
    status: z.string().optional().nullable(),
    remarks: z.string().optional().nullable(),
});
const NovelCheckSchema = z.object({
    performed: z.boolean(),
    salaryCreditsVerified: z.boolean().optional().nullable(),
    monthlyAverageBalance: z.number().optional().nullable(),
    salaryEntries: z.number().int().optional().nullable(),
});
const CashFlowAnalysisSchema = z.object({
    assessmentMethod: enumFromConst(ASSESSMENT_METHOD),
    cfaCompleted: z.boolean(),
});
const ObligationSchema = z.object({
    type: z.string(),
    amount: z.number(),
});
// Every term is nullable: a real LOS pull may predate sanction, and some
// terms (LTV, purpose) simply aren't carried by `underwriting_info`. Rules
// must treat null as "not reported" → inconclusive, never as zero.
const LoanDetailsSchema = z.object({
    sanctionedAmount: z.number().positive().nullable(),
    interestRatePct: z.number().nonnegative().nullable(),
    tenureMonths: z.number().int().positive().nullable(),
    emiAmount: z.number().positive().nullable(),
    ltvPct: z.number().nullable(),
    foirPct: z.number().nullable(),
    loanPurpose: z.string().optional().nullable(),
    obligationsConsidered: z.array(ObligationSchema).default([]),
});
const CompanyCategorizationSchema = z.object({
    employerName: z.string(),
    category: z.string(),
    maxLoanCapForCategory: z.number().positive(),
});
const DeviationSchema = z.object({
    deviationType: enumFromConst(DEVIATION_TYPE),
    status: enumFromConst(DEVIATION_STATUS),
    approverId: z.string().optional().nullable(),
    /** Name of the person who approved the deviation. */
    approvedBy: z.string().optional().nullable(),
    /** Approval authority level (e.g. Branch Head, Regional Head, Credit Committee). */
    approvalLevel: z.string().optional().nullable(),
    approverRemarks: z.string().optional().nullable(),
    mitigant: z.string().optional().nullable(),
    extra: z.record(z.unknown()).optional().nullable(),
});
// Sanction / disbursement dates only exist once those milestones are
// reached — a pre-disbursement LOS pull legitimately has neither, so both
// are optional and point 14 goes inconclusive when absent.
const DatesSchema = z.object({
    applicationDate: z.string().optional().nullable(),
    sanctionDate: z.string().optional().nullable(), // ISO YYYY-MM-DD
    disbursementDate: z.string().optional().nullable(), // ISO YYYY-MM-DD
});
const EAgreementSchema = z.object({
    interfaceStatus: enumFromConst(E_AGREEMENT_INTERFACE_STATUS),
    documentProcessedDate: z.string().optional().nullable(),
    signedDocumentStatus: z.string().optional().nullable(),
    legalDocId: z.string().optional().nullable(),
    signedDocumentUrl: z.string().url().optional().nullable(),
});
const LegalDetailSchema = z.object({
    /** Legal check status from LOS — typically 'positive' or 'negative'. */
    legalCheckStatus: z.string().optional().nullable(),
    /** Property name as recorded in LOS. */
    propertyName: z.string().optional().nullable(),
    /** Full property address as recorded in LOS. */
    propertyAddress: z.string().optional().nullable(),
    /** Property owner name as recorded in LOS. */
    ownerName: z.string().optional().nullable(),
    /** Whether the legal report was referenced/uploaded in LOS. */
    legalReportReferenced: z.boolean().optional().nullable(),
});
const ValuationDetailSchema = z.object({
    /** Property name as recorded in LOS valuation section. */
    propertyName: z.string().optional().nullable(),
    /** Valuation amount in INR as recorded in LOS. */
    valuationAmountInr: z.number().optional().nullable(),
    /** Valuation date in ISO YYYY-MM-DD format. */
    valuationDate: z.string().optional().nullable(),
    /** Name of the valuer/valuation firm. */
    valuerName: z.string().optional().nullable(),
    /** Property address as recorded in LOS valuation. */
    propertyAddress: z.string().optional().nullable(),
});
const PayslipMonthSchema = z.object({
    /** Month in YYYY-MM format. */
    month: z.string(),
    grossSalary: z.number(),
    netSalary: z.number(),
    pfDeduction: z.number().optional().nullable(),
    incomeTaxDeduction: z.number().optional().nullable(),
    professionalTax: z.number().optional().nullable(),
    otherDeductions: z.number().optional().nullable(),
});
const PayslipVerificationSchema = z.object({
    /** Were payslips provided by the borrower? */
    payslipsProvided: z.boolean(),
    /** Number of months of payslips provided. */
    monthsCovered: z.number(),
    /** Gross monthly salary from the latest payslip. */
    grossMonthlySalary: z.number(),
    /** Net monthly salary (take-home) from the latest payslip. */
    netMonthlySalary: z.number(),
    /** PF deduction per month. */
    pfDeduction: z.number().optional().nullable(),
    /** Income tax deduction per month. */
    incomeTaxDeduction: z.number().optional().nullable(),
    /** Professional tax per month. */
    professionalTax: z.number().optional().nullable(),
    /** Other deductions per month. */
    otherDeductions: z.number().optional().nullable(),
    /** Employer name as printed on the payslip. */
    employerNameOnPayslip: z.string(),
    /** Per-month payslip breakdown (optional — for month-by-month comparison). */
    monthlyBreakdown: z.array(PayslipMonthSchema).optional().nullable(),
});
// -------------------------------------------------------------------------
// Top-level schema — full LOS payload for one loan file
// -------------------------------------------------------------------------
// Sections below are optional because the real Axis LOS `underwriting_info`
// payload does not carry all of them, and its content varies by product and
// loan stage. A missing section means "LOS did not report this" and must
// drive an inconclusive verdict — never a fabricated pass or fail. Fully
// populated JSON uploads continue to validate unchanged.
export const LosApplicationDataSchema = z.object({
    loanNumber: z.string().min(1),
    productCode: z.string().min(1),
    branchCode: z.string().min(1).optional().nullable(),
    officerId: z.string().optional().nullable(),
    applicants: z.array(ApplicantSchema).min(1),
    apiStatusDetails: ApiStatusDetailsSchema.optional().nullable(),
    hunterVerification: HunterSchema.optional().nullable(),
    bqsCheck: BqsSchema.optional().nullable(),
    rcuVerification: RcuSchema.optional().nullable(),
    novelCheckBanking: NovelCheckSchema.optional().nullable(),
    cashFlowAnalysis: CashFlowAnalysisSchema.optional().nullable(),
    loanDetails: LoanDetailsSchema,
    companyCategorization: CompanyCategorizationSchema.optional().nullable(),
    deviations: z.array(DeviationSchema).default([]),
    sanctionConditions: z.array(z.string()).default([]),
    dates: DatesSchema,
    eAgreement: EAgreementSchema.optional().nullable(),
    /** Legal detail — optional for back-compat with existing LOS JSONs.
     *  When present, Point 10 (Legal Detail) cross-checks CAM vs LOS values. */
    legalDetail: LegalDetailSchema.optional().nullable(),
    /** Valuation detail — optional for back-compat with existing LOS JSONs.
     *  When present, Point 11 (Valuation Report) cross-checks CAM vs LOS values. */
    valuationDetail: ValuationDetailSchema.optional().nullable(),
    /** Payslip verification — optional. When present, Point 3 (Income Proof PL/BL)
     *  cross-checks payslip gross/net/deductions against CAM and bank credits. */
    payslipVerification: PayslipVerificationSchema.optional().nullable(),
});
//# sourceMappingURL=los.js.map